import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Layout, Paragraph, Fieldset, CustomRangeSlider, Textarea, Form, Input } from '@vwfs-bronson/bronson-react';
import { formatAsCurrency, formatAsNumber, MAX_REQUEST } from 'cpes-common';
import { useTranslation } from 'react-i18next';
import { useFinancialDetails } from 'components/contracts/financial-details/useFinancialDetails';
import { preventSubmit, RequestLoadingPlaceholder } from '@cp-shared-19/frontend-ui';
import moment from 'moment';
import { RefinancingFormValues } from '../refinancingFormikValues';

export type RefinanceSliderProps = {
    show: boolean;
    contractId: string;
    formValues: RefinancingFormValues;
    setFormValues: (values: Partial<RefinancingFormValues>) => void;
};

export const RefinanceSlider: React.FC<RefinanceSliderProps> = ({ show, contractId, formValues, setFormValues }) => {
    const { t } = useTranslation('digital-renewal');
    const sliderAmountCurrency = formValues.sliderAmountCurrency;
    const [inputCurrencyValue, setInputCurrencyValue] = useState<number>(sliderAmountCurrency);
    const [currencySliderValue, setCurrencySliderValue] = useState<number>(sliderAmountCurrency);
    const lastCurrencyDragValueRef = useRef<number>(sliderAmountCurrency);

    const { data: financialDetails, isLoading } = useFinancialDetails(contractId);

    useEffect(() => {
        setInputCurrencyValue(sliderAmountCurrency);
        setCurrencySliderValue(sliderAmountCurrency);
        lastCurrencyDragValueRef.current = sliderAmountCurrency;
    }, [sliderAmountCurrency]);

    if (!show) {
        return null;
    }

    if (isLoading) {
        return <RequestLoadingPlaceholder />;
    }

    if (!financialDetails || !('pendingCapital' in financialDetails)) {
        return null;
    }

    const { pendingCapital, startDate, endDate } = financialDetails;

    const currentContractMonths = moment(endDate).diff(startDate, 'months');
    const maxSliderMonths = Math.max(0, Math.floor((96 - currentContractMonths) / 12) * 12);
    const maxSliderCurrency = Math.max(0, Math.round(pendingCapital ?? 0));

    const getSingleSliderValue = (value: string | number | (string | number)[]): string => {
        if (Array.isArray(value)) {
            return String(value[0] ?? '0');
        }

        return String(value ?? '0');
    };

    const parseSliderNumber = (value: string): number => {
        const parsedValue = parseFloat(value);
        return Number.isFinite(parsedValue) ? parsedValue : 0;
    };

    const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

    const handleChangeForSliderValueMonths = (value: string): void => {
        const intValue = parseSliderNumber(value);
        const normalizedValue = clamp(intValue, 0, maxSliderMonths);

        setFormValues({ sliderAmount: normalizedValue });
    };

    const handleSliderCurrencyChange = (value: string): void => {
        const floatValue = parseSliderNumber(value);
        const normalizedValue = clamp(floatValue, 0, maxSliderCurrency);

        // Keep drag value in ref to avoid expensive rerenders on each pointer move.
        lastCurrencyDragValueRef.current = normalizedValue;
    };

    const handleSliderCurrencySet = (value: string): void => {
        const dragValue = Number.isFinite(lastCurrencyDragValueRef.current)
            ? lastCurrencyDragValueRef.current
            : parseSliderNumber(value);
        const intValue = Math.round(dragValue);
        const normalizedValue = clamp(intValue, 0, maxSliderCurrency);

        setInputCurrencyValue(normalizedValue);
        setCurrencySliderValue(normalizedValue);
        setFormValues({ sliderAmountCurrency: normalizedValue });
    };

    const handleChangeRequest = (event: FormEvent<HTMLInputElement>): void => {
        event.preventDefault();
        setFormValues({ request: event.currentTarget.value });
    };

    const handleChangeForInputCurrencyValue = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const intValue = Math.round(parseSliderNumber(event.target.value));
        const normalizedValue = clamp(intValue, 0, maxSliderCurrency);

        if (!event.target.value || !Number.isFinite(parseFloat(event.target.value))) {
            setInputCurrencyValue(0);
            setCurrencySliderValue(0);
            lastCurrencyDragValueRef.current = 0;
            setFormValues({ sliderAmountCurrency: 0 });
        } else {
            setInputCurrencyValue(normalizedValue);
            setCurrencySliderValue(normalizedValue);
            lastCurrencyDragValueRef.current = normalizedValue;
            setFormValues({ sliderAmountCurrency: normalizedValue });
        }
    };

    return (
        <Form onSubmit={e => preventSubmit(e)}>
            <Paragraph>
                <span
                    dangerouslySetInnerHTML={{
                        __html: t('finance.refinance-or-payment-page.radio-button.refinancing-text'),
                    }}
                />
            </Paragraph>
            <Fieldset className="u-text-center">
                <Fieldset.Row>
                    <Layout.Item default="1/1" s="1/1">
                        <CustomRangeSlider
                            className="fix-range-slider-knob"
                            value={formValues.sliderAmount}
                            range={{ min: 0, max: maxSliderMonths }}
                            step={12}
                            tooltips={{
                                to: (value: number): string =>
                                    `${formatAsNumber(+value)} ${t('finance.refinance-or-payment-page.slider.months')}`,
                            }}
                            onSet={(value: string | number | (string | number)[]): void =>
                                handleChangeForSliderValueMonths(getSingleSliderValue(value))
                            }
                        />
                    </Layout.Item>
                </Fieldset.Row>
                <Fieldset.Row>
                    <Layout.Item className="u-text-center" default="1/4" s={'1/1'}>
                        <Input
                            testId="inputCurrencyValue"
                            type="text"
                            addonText={'€'}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                                handleChangeForInputCurrencyValue(event);
                            }}
                            value={inputCurrencyValue}
                        />
                    </Layout.Item>
                    <Layout.Item default="1/1" s="1/1">
                        {pendingCapital && (
                            <CustomRangeSlider
                                className="fix-range-slider-knob"
                                value={currencySliderValue}
                                range={{ min: 0, max: maxSliderCurrency }}
                                step={1}
                                tooltips={{
                                    to: (value: number): string => formatAsCurrency(+value),
                                }}
                                onChange={(value: string | number | (string | number)[]): void =>
                                    handleSliderCurrencyChange(getSingleSliderValue(value))
                                }
                                onSet={(value: string | number | (string | number)[]): void =>
                                    handleSliderCurrencySet(getSingleSliderValue(value))
                                }
                            />
                        )}
                    </Layout.Item>
                </Fieldset.Row>

                <Fieldset.Row>
                    <Layout.Item default="1/1" s="1/1">
                        <p style={{ textAlign: 'left' }}>{t('finance.refinance-or-payment-page.text-area')}</p>
                        <Textarea
                            testId="request"
                            inputMode="text"
                            maxCounter={MAX_REQUEST}
                            charCounter
                            value={formValues.request}
                            onInput={e => handleChangeRequest((e as unknown) as FormEvent<HTMLInputElement>)}
                        />
                    </Layout.Item>
                </Fieldset.Row>
            </Fieldset>
        </Form>
    );
};
